import type { RigidBody } from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import {
  CHUNK_SIZE,
  HEIGHT_CELLS,
  heightAt,
  ISLAND_CHUNKS,
  ISLAND_MIN,
  sampleChunkHeights,
} from './Heightmap';

export const RESIDENT_RADIUS = 1;
export const CHUNK_MIN = ISLAND_MIN / CHUNK_SIZE;
export const CHUNK_MAX = CHUNK_MIN + ISLAND_CHUNKS - 1;

const VISUAL_SINK = 0.02;
const TILE_HALF_Y = 0.5;

export type ChunkCoord = { cx: number; cz: number };

export function chunkIndex(v: number): number {
  return Math.floor(v / CHUNK_SIZE);
}

export function worldToChunk(x: number, z: number): ChunkCoord {
  return { cx: chunkIndex(x), cz: chunkIndex(z) };
}

export function chunkKey(cx: number, cz: number): string {
  return `${cx},${cz}`;
}

export function parseChunkKey(key: string): ChunkCoord {
  const sep = key.indexOf(',');
  const cx = Number(key.slice(0, sep));
  const cz = Number(key.slice(sep + 1));
  if (!Number.isInteger(cx) || !Number.isInteger(cz)) {
    throw new Error(`bad chunk key: ${key}`);
  }
  return { cx, cz };
}

export function chunkOrigin(cx: number, cz: number): { x: number; z: number } {
  return { x: cx * CHUNK_SIZE, z: cz * CHUNK_SIZE };
}

export function chunkCenter(cx: number, cz: number): { x: number; z: number } {
  return { x: cx * CHUNK_SIZE + CHUNK_SIZE * 0.5, z: cz * CHUNK_SIZE + CHUNK_SIZE * 0.5 };
}

export function inIslandChunk(cx: number, cz: number): boolean {
  return cx >= CHUNK_MIN && cx <= CHUNK_MAX && cz >= CHUNK_MIN && cz <= CHUNK_MAX;
}

export function clampChunkIndex(c: number): number {
  if (c < CHUNK_MIN) {
    return CHUNK_MIN;
  }
  if (c > CHUNK_MAX) {
    return CHUNK_MAX;
  }
  return c;
}

/** 3×3 (radius 1) around the player. Off-island cells are dropped. */
export function residentKeysAt(x: number, z: number, radius = RESIDENT_RADIUS): string[] {
  const cx = clampChunkIndex(chunkIndex(x));
  const cz = clampChunkIndex(chunkIndex(z));
  const keys: string[] = [];
  for (let dz = -radius; dz <= radius; dz++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const ix = cx + dx;
      const iz = cz + dz;
      if (inIslandChunk(ix, iz)) {
        keys.push(chunkKey(ix, iz));
      }
    }
  }
  return keys;
}

export class Chunk {
  readonly key: string;
  readonly cx: number;
  readonly cz: number;
  private mesh: THREE.Mesh | null = null;
  private fieldBody: RigidBody | null = null;
  private tileBody: RigidBody | null = null;

  constructor(cx: number, cz: number) {
    this.cx = cx;
    this.cz = cz;
    this.key = chunkKey(cx, cz);
  }

  load(scene: THREE.Object3D, physics: PhysicsWorld): void {
    if (this.mesh) {
      return;
    }
    const center = chunkCenter(this.cx, this.cz);
    this.mesh = makeVisual(this.cx, this.cz, this.key, center);
    scene.add(this.mesh);

    const heights = sampleChunkHeights(this.cx, this.cz);
    const field = physics.addStaticHeightfield(
      HEIGHT_CELLS,
      HEIGHT_CELLS,
      heights,
      { x: CHUNK_SIZE, y: 1, z: CHUNK_SIZE },
      center.x,
      0,
      center.z,
    );
    this.fieldBody = field.body;

    // Tiled plate under the field so a bad heightfield cannot drop the KCC.
    const tile = physics.addStaticCuboid(
      CHUNK_SIZE * 0.5,
      TILE_HALF_Y,
      CHUNK_SIZE * 0.5,
      center.x,
      -TILE_HALF_Y,
      center.z,
    );
    this.tileBody = tile.body;
  }

  unload(scene: THREE.Object3D, physics: PhysicsWorld): void {
    if (this.mesh) {
      scene.remove(this.mesh);
      this.mesh.geometry.dispose();
      const mat = this.mesh.material;
      if (Array.isArray(mat)) {
        for (const item of mat) {
          item.dispose();
        }
      } else {
        mat.dispose();
      }
      this.mesh = null;
    }
    if (this.fieldBody) {
      physics.removeRigidBody(this.fieldBody);
      this.fieldBody = null;
    }
    if (this.tileBody) {
      physics.removeRigidBody(this.tileBody);
      this.tileBody = null;
    }
  }
}

function makeVisual(
  cx: number,
  cz: number,
  key: string,
  center: { x: number; z: number },
): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE, HEIGHT_CELLS, HEIGHT_CELLS);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const wx = center.x + pos.getX(i);
      const wz = center.z + pos.getZ(i);
      pos.setY(i, heightAt(wx, wz) - VISUAL_SINK);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }
  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      color: 0x4a4030,
      roughness: 0.96,
      metalness: 0.02,
    }),
  );
  mesh.position.set(center.x, 0, center.z);
  mesh.receiveShadow = true;
  mesh.name = `CHUNK_${cx}_${cz}`;
  mesh.userData.chunk = key;
  return mesh;
}
